import React from 'react';

import { Box, Button, Typography } from '@mui/material';

import { Field, Form } from '@hrlogics/react-components/form';

import {
  UserDataShapeForNewEmployeeOnDataDisputeCreation,
  useCreateDataDisputeOnEmployeeSignupMutation,
} from 'src/api';
import { Modal } from 'src/components/common';
import { TextField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';
import { useModalV2 } from 'src/hooks/shared';
import { ModalV2Props } from 'src/types/modalV2';

import { FormField } from './constants';
import { FormValues } from './types';
import { createDataDisputeValidationSchema } from './validation';

type CreateDataDisputeOnEmployeeSignupModalProps = {
  employeeSignupToken: string;
  companyId: string;
  userData: UserDataShapeForNewEmployeeOnDataDisputeCreation;
};
export const CreateDataDisputeOnEmployeeSignupModal: React.FC<
  ModalV2Props<CreateDataDisputeOnEmployeeSignupModalProps>
> = ({ id, employeeSignupToken, companyId, userData }) => {
  const { closeModal, switchClosability } = useModalV2();

  const { createApolloContext } = useNotification();

  const [createDataDisputeOnEmployeeSignup] = useCreateDataDisputeOnEmployeeSignupMutation({
    context: createApolloContext({ successMessage: 'Data Dispute created successfully' }),
  });

  const onCloseModal = React.useCallback(() => {
    closeModal(id);
  }, [closeModal, id]);

  const handleSubmit = React.useCallback(
    async (formValues: FormValues) => {
      switchClosability(false);

      try {
        await createDataDisputeOnEmployeeSignup({
          variables: {
            employeeSignupToken,
            userData,
            companyId,
            reason: formValues.reason,
          },
          awaitRefetchQueries: false,
        });
      } catch (error) {
        console.error(error);
      }

      switchClosability(true);
      onCloseModal();
    },
    [
      switchClosability,
      createDataDisputeOnEmployeeSignup,
      employeeSignupToken,
      userData,
      companyId,
      onCloseModal,
    ],
  );

  return (
    <Modal
      dialogProps={{
        onClose: onCloseModal,
        open: true,
      }}
      maxWidth={550}
    >
      <Form<FormValues>
        subscription={{ valid: true, values: true, submitting: true }}
        onSubmit={handleSubmit}
        validationSchema={createDataDisputeValidationSchema}
      >
        {({ submitting, valid }) => (
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" fontSize="24px">
              Confirm you want to submit a Data Dispute
            </Typography>
            <Typography variant="body1" fontSize="16px" color="text.secondary">
              Please, state the reason of Data Dispute
            </Typography>
            <Field
              name={FormField.reason}
              fullWidth
              required
              label="Note to Employer"
              component={TextField}
            />
            <Box display="flex" gap={3} justifyContent="center" alignItems="center">
              <Button
                color="primary"
                variant="outlined"
                onClick={onCloseModal}
                disabled={submitting}
              >
                cancel
              </Button>
              <Button
                color="error"
                variant="contained"
                type="submit"
                disabled={submitting || !valid}
                loading={submitting}
              >
                yes, dispute
              </Button>
            </Box>
          </Box>
        )}
      </Form>
    </Modal>
  );
};
